import React from 'react';
import styled from 'styled-components';
import { FaFacebookF, FaTwitter, FaInstagram, FaYoutube, FaEnvelope } from 'react-icons/fa';
import { FaWhatsapp } from 'react-icons/fa6';
import { tokens } from '../../design/tokens';
import { useSocialSettings } from '../../hooks/useSocialSettings';

const Wrapper = styled.footer<{ $isAdmin?: boolean }>`
  background: ${tokens.colors.surface};
  color: ${tokens.colors.text};
  margin-top: auto;
  border-top: 1px solid ${tokens.colors.border};
  margin-left: ${props => props.$isAdmin ? '250px' : '0'};

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;

const Container = styled.div<{ $isAdmin?: boolean }>`
  width: 100%;
  margin: 0;
  padding: ${tokens.spacing.xl};
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: ${tokens.spacing.xl};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: ${tokens.spacing.lg};
    padding: ${tokens.spacing.lg};
  }
`;

const Column = styled.div``;

const Title = styled.h3`
  font-size: ${tokens.typography.h3.size};
  line-height: ${tokens.typography.h3.line};
  font-weight: ${tokens.typography.h3.weight};
  margin: 0 0 ${tokens.spacing.md} 0;
`;

const Link = styled.a`
  display: block;
  color: ${tokens.colors.text};
  margin-bottom: ${tokens.spacing.sm};
  text-decoration: none;
  transition: color 0.2s ease;
  
  &:hover { 
    color: ${tokens.colors.primary}; 
    text-decoration: underline;
  }
  
  &:focus-visible {
    outline: 2px solid ${tokens.colors.primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const ComplianceLinks = styled.div`
  margin-bottom: ${tokens.spacing.xl};
`;

const ComplianceNav = styled.nav`
  display: flex;
  flex-wrap: wrap;
  gap: ${tokens.spacing.md};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${tokens.spacing.sm};
  }
`;

const ContactRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: ${tokens.spacing.md};
  margin-bottom: ${tokens.spacing.xl};

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: ${tokens.spacing.sm};
  }
`;

const ContactLink = styled.a<{ $hasContact?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.xs};
  color: ${props => props.$hasContact ? tokens.colors.text : tokens.colors.border};
  text-decoration: none;
  font-size: ${tokens.typography.body.size};
  transition: color 0.2s ease;
  
  &:hover { 
    color: ${props => props.$hasContact ? tokens.colors.primary : tokens.colors.border};
    text-decoration: ${props => props.$hasContact ? 'underline' : 'none'};
  }
  
  &:focus-visible {
    outline: 2px solid ${tokens.colors.primary};
    outline-offset: 2px;
    border-radius: 2px;
  }
`;

const SocialRow = styled.div`
  display: flex;
  gap: ${tokens.spacing.md};
`;

const SocialLink = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${tokens.colors.surfaceMuted};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${tokens.shadow.sm};
  transition: all 0.2s ease;
  
  &:hover { 
    background: ${tokens.colors.primary}; 
    color: ${tokens.colors.textOnDark};
    transform: translateY(-1px);
    box-shadow: ${tokens.shadow.md};
  }
  
  &:focus-visible {
    outline: 2px solid ${tokens.colors.primary};
    outline-offset: 2px;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.sm};
  color: ${tokens.colors.border};
  font-size: ${tokens.typography.caption.size};
`;

const ErrorState = styled.div`
  display: flex;
  align-items: center;
  gap: ${tokens.spacing.sm};
  color: ${tokens.colors.error};
  font-size: ${tokens.typography.caption.size};
`;

const BottomBar = styled.div<{ $isAdmin?: boolean }>`
  border-top: 1px solid ${tokens.colors.border};
  padding: ${tokens.spacing.lg} ${tokens.spacing.xl};
  text-align: center;
  font-size: ${tokens.typography.caption.size};
  line-height: ${tokens.typography.caption.line};
  color: ${tokens.colors.border};

  @media (max-width: 768px) {
    padding: ${tokens.spacing.md};
  }
`;

export const Footer: React.FC = () => {
  const FaFacebookFComponent = FaFacebookF as React.ComponentType;
  const FaTwitterComponent = FaTwitter as React.ComponentType;
  const FaInstagramComponent = FaInstagram as React.ComponentType;
  const FaYoutubeComponent = FaYoutube as React.ComponentType;
  const FaEnvelopeComponent = FaEnvelope as React.ComponentType;
  const FaWhatsappComponent = FaWhatsapp as React.ComponentType;

  const {
    settings: socialSettings,
    loading,
    error,
    whatsappContact,
    contactEmail
  } = useSocialSettings();

  const isAdmin = typeof window !== 'undefined' && window.location.pathname.startsWith('/admin');

  const renderContactInfo = () => {
    if (loading) {
      return (
        <LoadingState>
          <span>Cargando información de contacto...</span>
        </LoadingState>
      );
    }

    if (error) {
      return (
        <ErrorState>
          <span>Error al cargar contactos</span>
        </ErrorState>
      );
    }

    return (
      <ContactRow>
        {/* WhatsApp Link - Simple version */}
        {whatsappContact && (
          <ContactLink
            href={`https://wa.me/${whatsappContact.replace(/\D/g, '')}`}
            target="_blank"
            rel="noopener noreferrer"
            $hasContact={true}
            aria-label={`Abrir WhatsApp para ${whatsappContact}`}
          >
            <FaWhatsappComponent />
            <span>WhatsApp</span>
          </ContactLink>
        )}

        {/* E-mail de contato */}
        {contactEmail && (
          <ContactLink
            href={`mailto:${contactEmail}`}
            className="contact-email"
            $hasContact={true}
            aria-label={`Enviar correo a ${contactEmail}`}
          >
            <FaEnvelopeComponent />
            <span>{contactEmail}</span>
          </ContactLink>
        )}

        {/* Fallback quando não há dados do backend */}
        {!whatsappContact && !contactEmail && !loading && (
          <ContactLink $hasContact={false}>
            <FaEnvelopeComponent />
            <span>contato@cafetutoriales.com</span>
          </ContactLink>
        )}
      </ContactRow>
    );
  };

  const renderSocialLinks = () => {
    // Se há dados do backend, usar URLs reais
    if (socialSettings && !loading && !error) {
      const links = [];

      if (socialSettings.facebook_url) {
        links.push(
          <SocialLink
            key="facebook"
            href={socialSettings.facebook_url}
            aria-label="Facebook"
            title="Facebook"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaFacebookFComponent />
          </SocialLink>
        );
      }

      if (socialSettings.twitter_url) {
        links.push(
          <SocialLink
            key="twitter"
            href={socialSettings.twitter_url}
            aria-label="Twitter"
            title="Twitter"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaTwitterComponent />
          </SocialLink>
        );
      }

      if (socialSettings.instagram_url) {
        links.push(
          <SocialLink
            key="instagram"
            href={socialSettings.instagram_url}
            aria-label="Instagram"
            title="Instagram"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaInstagramComponent />
          </SocialLink>
        );
      }

      if (socialSettings.youtube_url) {
        links.push(
          <SocialLink
            key="youtube"
            href={socialSettings.youtube_url}
            aria-label="YouTube"
            title="YouTube"
            target="_blank"
            rel="noopener noreferrer"
          >
            <FaYoutubeComponent />
          </SocialLink>
        );
      }

      // Se não encontrou nenhuma URL válida, mostrar ícones de teste
      if (links.length === 0) {
        return renderTestIcons();
      }

      return <SocialRow>{links}</SocialRow>;
    }

    // Se não há dados ou está carregando, mostrar ícones de teste
    return renderTestIcons();
  };

  const renderTestIcons = () => {
    return (
      <SocialRow>
        <SocialLink
          key="test-facebook"
          href="https://facebook.com"
          aria-label="Facebook (Prueba)"
          title="Facebook (Prueba)"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaFacebookFComponent />
        </SocialLink>
        <SocialLink
          key="test-instagram"
          href="https://instagram.com"
          aria-label="Instagram (Prueba)"
          title="Instagram (Prueba)"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaInstagramComponent />
        </SocialLink>
        <SocialLink
          key="test-twitter"
          href="https://twitter.com"
          aria-label="Twitter (Prueba)"
          title="Twitter (Prueba)"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaTwitterComponent />
        </SocialLink>
        <SocialLink
          key="test-youtube"
          href="https://youtube.com"
          aria-label="YouTube (Prueba)"
          title="YouTube (Prueba)"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FaYoutubeComponent />
        </SocialLink>
      </SocialRow>
    );
  };

  return (
    <Wrapper $isAdmin={isAdmin} aria-label="Pie de página">
      <a href="#main" className="skip-link">Ir al contenido</a>
      <Container $isAdmin={isAdmin}>
        {/* Links de Compliance */}
        <Column>
          <Title>Información Legal</Title>
          <ComplianceLinks>
            <ComplianceNav aria-label="Enlaces de cumplimiento y soporte">
              <Link href="/politica-privacidade" aria-label="Política de Privacidad">
                Política de Privacidad
              </Link>
              <Link href="/termos-uso" aria-label="Términos de Uso">
                Términos de Uso
              </Link>
              <Link href="/ajuda-suporte" aria-label="Ayuda y Soporte">
                Ayuda y Soporte
              </Link>
              <Link href="/como-baixar-app" aria-label="Cómo descargar la app">
                Cómo Descargar la App
              </Link>
            </ComplianceNav>
          </ComplianceLinks>
        </Column>

        {/* Informações de Contato */}
        <Column>
          <Title>Contacto</Title>
          {renderContactInfo()}
        </Column>

        {/* Redes Sociais */}
        <Column>
          <Title>Redes Sociales</Title>
          {renderSocialLinks()}
        </Column>

        {/* Direitos Autorais */}
        <Column>
          <Title>Derechos de Autor</Title>
          <div>© {new Date().getFullYear()} Café Tutoriales. Todos los derechos reservados.</div>
        </Column>
      </Container>
      <BottomBar $isAdmin={isAdmin}>
        Construido con enfoque en accesibilidad, rendimiento y experiencia de usuario.
      </BottomBar>
    </Wrapper>
  );
};

export default Footer;